/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import AIReview from 'bundles/unified-description-page-common/components/modules/reviews/AIReview';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import type { RatingsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';

import _t from 'i18n!nls/xdp';

export const NUM_REVIEWS_TO_DISPLAY = 3;

type PropsToComponent = {
  title: string;
  ratingsInfo?: RatingsFragment | null;
  reviews?: Array<React.ReactNode> | null;
  ratingsBreakdown?: React.ReactNode | null;
  cta?: React.ReactNode | null;
  anchorId?: string;
  courseId?: string;
};

const styles = {
  contentWrapper: css`
    padding: var(--cds-spacing-400) 0 var(--cds-spacing-400) 0;

    ${breakpoints.down('sm')} {
      display: inline-grid;
      padding: 0;
    }
  `,
  headerLabels: css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  reviews: css`
    display: inline-grid;
    gap: var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      gap: var(--cds-spacing-150);
    }
  `,
  cta: css`
    a {
      color: var(--cds-color-neutral-primary) !important; /* Required to override Link styling */
    }
  `,
  streamlinedCTA: css`
    margin-top: var(--cds-spacing-100);

    a {
      text-decoration: none;

      :hover {
        text-decoration: underline;
      }
    }

    span {
      ${{ ...typography2.actionSecondary }};
    }
  `,
};

const ReviewsModule = ({
  title,
  ratingsInfo,
  reviews,
  ratingsBreakdown,
  cta,
  anchorId,
  courseId,
}: PropsToComponent) => {
  if (!reviews || !reviews?.length || !ratingsInfo || !title) {
    return null;
  }

  return (
    <Section anchorId={anchorId}>
      <TrackedDiv
        trackingName="reviews_module"
        withVisibilityTracking={true}
        requireFullyVisible={false}
        trackClicks={false}
      >
        <PageGridContainer>
          <Grid container item md={4} direction="column">
            <div css={styles.headerLabels}>
              <Typography variant="h2semibold" component="h2">
                {title}
              </Typography>
            </div>
            <Grid css={styles.contentWrapper}>{ratingsBreakdown}</Grid>
          </Grid>
          <Grid container item md={8}>
            <AIReview />
            <Grid container item css={styles.headerLabels}>
              {ratingsInfo?.ratingCount && NUM_REVIEWS_TO_DISPLAY <= ratingsInfo.ratingCount && (
                <Typography2 component="p" color="supportText">
                  {_t(`Showing #{numDisplayedReviews} of #{ratingCount}`, {
                    numDisplayedReviews: NUM_REVIEWS_TO_DISPLAY,
                    ratingCount: ratingsInfo.ratingCount,
                  })}
                </Typography2>
              )}
            </Grid>
            <Grid item css={[styles.reviews, styles.contentWrapper]}>
              {reviews.slice(0, NUM_REVIEWS_TO_DISPLAY)}
              {cta && <div css={styles.cta}>{cta}</div>}
            </Grid>
          </Grid>
        </PageGridContainer>
      </TrackedDiv>
    </Section>
  );
};

export default ReviewsModule;
